import { z } from "zod";

import {BidPackageSchema} from "@/models/BidPackage";
import utcToLocal from '@/use/utcToLocal';
import {UserSchema} from "@/models/User";
import {ProjectContactSchema} from "@/models/ProjectContact";
import {ProjectBidderSchema} from "@/models/ProjectBidder";
import {NoticeSchema} from "@/models/Notice";
import {ClientSchema} from "@/models/Client";
import formatRFQDateTime from "@/use/formatRFQDateTime";

const dateRegex = new RegExp("^\\d{4}-\\d{2}-\\d{2}$");
const timeRegex = new RegExp("^[0-2][0-9](:[0-5][0-9]){2}$");

export const ProjectSchema = z.object({
    id: z.coerce.number(),
    name: z.string().optional(),
    bid_phase_name: z.string().nullable().optional(),
    client_id: z.coerce.number().optional(),
    business_unit_id: z.string().optional(),
    number: z.string().optional(),
    address: z.string().nullable().optional(),
    city: z.string().nullable().optional(),
    state: z.string().nullable().optional(),
    zip: z.string().nullable().optional(),
    location: z.string().nullable().optional(),
    confirmed: z.number().nullable().default(3).optional(),
    scope: z.string().nullable().optional(),
    exclusions: z.string().nullable().optional(),
    other_info: z.string().nullable().optional(),
    has_bid_packages: z.coerce.boolean().nullable().optional(),
    invited: z.coerce.boolean().nullable().optional(),
    open_invitation: z.coerce.boolean().nullable().optional(),
    is_secure_project: z.coerce.boolean().nullable().optional(),
    has_secure_access: z.coerce.boolean().nullable().optional(),
    limit_opr_to_awarded: z.coerce.boolean().nullable().optional(),
    show_bid_upload: z.boolean().optional().nullable().optional(),
    accepting_bid_submissions: z.coerce.boolean().default(true).optional(),
    archived_at: z.string().nullable().optional(),
    is_bidding: z.coerce.number().default(0).optional(),
    is_bid_uploaded: z.coerce.boolean().default(false).optional(),

    sub_bid_date: z.string().regex(dateRegex).transform((val:string) => formatRFQDateTime(val, '')).nullable(),
    sub_bid_time: z.string().regex(timeRegex).transform((val:string) => formatRFQDateTime('', val)).nullable(),
    sub_bid_due: z.string().optional().nullable(),

    rfi_due_date: z.string().regex(dateRegex).transform((val:string) => formatRFQDateTime(val, '')).nullable().optional(),
    rfi_due_time: z.string().regex(timeRegex).transform((val:string) => formatRFQDateTime('', val)).nullable().optional(),
    rfi_due_at: z.string().optional().nullable(),

    site_meeting_date: z.string().regex(dateRegex).transform((val:string) => formatRFQDateTime(val, '')).nullable().optional(),
    site_meeting_time: z.string().regex(timeRegex).transform((val:string) => formatRFQDateTime('', val)).nullable().optional(),
    site_meeting_at: z.string().optional().nullable().optional(),

    created_at: z.string().transform((val:string) => utcToLocal(val)).nullable().optional(),

    bidders: z.array(ProjectBidderSchema).optional().default([]),
    bid_packages: z.array(BidPackageSchema).optional().default([]),
    client: ClientSchema.optional().default(ClientSchema.parse({})),
    estimators: z.array(UserSchema).optional().default([]),
    notices: z.array(NoticeSchema).optional().default([]),
    project_contacts: z.array(ProjectContactSchema).optional().default([]),
});


export type Project = z.infer<typeof ProjectSchema>;
